import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Header from '../../components/Header'
import { useTheme } from '../../contexts/ThemeContext'
import { useAuth } from '../../contexts/AuthContext'
import { coursesAPI } from '../../config/api'
import 'primeicons/primeicons.css'

export default function ChallengeDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { theme } = useTheme()
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [challenge, setChallenge] = useState<any>(null)
  const [submission, setSubmission] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [image, setImage] = useState<File | null>(null)
  const [reflection, setReflection] = useState('')
  const [imagePreview, setImagePreview] = useState<string | null>(null)

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }

    const fetchChallenge = async () => {
      try {
        setLoading(true)
        const data = await coursesAPI.getCurriculumLessonDetail(Number(id))
        setChallenge(data)
        
        // Lấy submission nếu có
        try {
          const subData = await coursesAPI.getMySubmission(Number(id))
          setSubmission(subData)
          if (subData.image_url) {
            setImagePreview(subData.image_url)
          }
          if (subData.reflection) {
            setReflection(subData.reflection)
          }
        } catch (error) {
          // Chưa có submission
        }
      } catch (error) {
        // Error fetching challenge
      } finally {
        setLoading(false)
      }
    }

    fetchChallenge()
  }, [id, isAuthenticated, navigate])

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setImage(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSubmit = async () => {
    if (!image || !reflection.trim()) {
      return
    }

    try {
      setSubmitting(true)
      await coursesAPI.submitChallenge(Number(id), {
        image,
        reflection: reflection.trim()
      })
      navigate('/challenges')
    } catch (error: any) {
      // Error submitting challenge
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <main className={`min-h-screen ${theme === 'dark' ? 'bg-black' : 'bg-gray-200'}`}>
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold-400 mx-auto mb-4"></div>
            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Đang tải...</p>
          </div>
        </div>
      </main>
    )
  }

  if (!challenge) {
    return (
      <main className={`min-h-screen ${theme === 'dark' ? 'bg-black' : 'bg-gray-200'}`}>
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Không tìm thấy thử thách</p>
        </div>
      </main>
    )
  }

  const submissionStatus = submission?.status || null
  const isActive = challenge.is_active !== false // Mặc định là true nếu không có

  return (
    <main className={`min-h-screen ${theme === 'dark' ? 'bg-black' : 'bg-gray-200'}`}>
      <Header />

      {/* Header Section */}
      <section className={`py-8 md:py-12 px-4 ${theme === 'dark' ? 'bg-gradient-to-b from-black to-gold-900/10' : 'bg-gradient-to-b from-gray-200 to-gold-100/20'}`}>
        <div className="container mx-auto max-w-4xl">
          <button
            onClick={() => navigate('/challenges')}
            className={`mb-4 flex items-center gap-2 ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
          >
            <i className="pi pi-arrow-left"></i>
            <span>Quay lại</span>
          </button>
          
          <h1 className={`text-3xl md:text-4xl font-bold mb-2 ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
            {challenge.title}
          </h1>
          {challenge.description && (
            <p className={`text-base md:text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              {challenge.description}
            </p>
          )}
        </div>
      </section>

      {/* Content Section */}
      <section className={`py-8 md:py-12 px-4 ${theme === 'dark' ? 'bg-black' : 'bg-gray-200'}`}>
        <div className="container mx-auto max-w-4xl">
          {!isActive ? (
            <div className={`p-8 rounded-xl text-center ${theme === 'dark' ? 'bg-black/50 border border-gray-700' : 'bg-white border border-gray-300'}`}>
              <i className="pi pi-lock text-6xl text-gold-400 mb-4"></i>
              <h2 className={`text-2xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Thử thách này chưa được mở
              </h2>
              <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Video thử thách này chưa được kích hoạt. Vui lòng quay lại sau.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Video Link Button */}
              {challenge.video_url && (
                <div className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-black/50 border border-gold-500/20' : 'bg-white border border-gold-400/20'}`}>
                  <a
                    href={challenge.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-full px-6 py-4 rounded-lg font-bold text-lg transition-all flex items-center justify-center gap-3 ${
                      theme === 'dark'
                        ? 'bg-gold-gradient text-white hover:shadow-lg hover:shadow-gold-500/50'
                        : 'bg-gold-gradient text-white hover:shadow-lg hover:shadow-gold-400/50'
                    } transform hover:scale-105 active:scale-95`}
                  >
                    <i className="pi pi-play text-2xl"></i>
                    <span>Xem video</span>
                    <i className="pi pi-external-link text-lg"></i>
                  </a>
                </div>
              )}

              {/* Submission Section */}
              {submissionStatus === 'approved' ? (
                <div className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-green-500/20 border border-green-500/50' : 'bg-green-100 border border-green-400'}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <i className="pi pi-check-circle text-3xl text-green-500"></i>
                    <h2 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      Đã hoàn thành và được duyệt
                    </h2>
                  </div>
                  {submission.image_url && (
                    <div className="mb-4">
                      <img
                        src={submission.image_url}
                        alt="Submission"
                        className="w-full max-w-md rounded-lg"
                      />
                    </div>
                  )}
                  {submission.reflection && (
                    <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-black/50' : 'bg-white'}`}>
                      <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        {submission.reflection}
                      </p>
                    </div>
                  )}
                  {submission.points_awarded > 0 && (
                    <p className={`mt-4 font-semibold ${theme === 'dark' ? 'text-gold-400' : 'text-gold-600'}`}>
                      +{submission.points_awarded} điểm đã được cộng
                    </p>
                  )}
                </div>
              ) : submissionStatus === 'pending' ? (
                <div className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-yellow-500/20 border border-yellow-500/50' : 'bg-yellow-100 border border-yellow-400'}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <i className="pi pi-clock text-3xl text-yellow-500"></i>
                    <h2 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      Đang chờ duyệt
                    </h2>
                  </div>
                  {submission.image_url && (
                    <div className="mb-4">
                      <img
                        src={submission.image_url}
                        alt="Submission"
                        className="w-full max-w-md rounded-lg"
                      />
                    </div>
                  )}
                  {submission.reflection && (
                    <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-black/50' : 'bg-white'}`}>
                      <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        {submission.reflection}
                      </p>
                    </div>
                  )}
                  <p className={`mt-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Submission của bạn đang chờ admin duyệt.
                  </p>
                </div>
              ) : submissionStatus === 'rejected' ? (
                <div className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-red-500/20 border border-red-500/50' : 'bg-red-100 border border-red-400'}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <i className="pi pi-times-circle text-3xl text-red-500"></i>
                    <h2 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      Submission bị từ chối
                    </h2>
                  </div>
                  {submission.rejection_reason && (
                    <p className={`mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Lý do: {submission.rejection_reason}
                    </p>
                  )}
                  <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Vui lòng gửi lại submission mới.
                  </p>
                </div>
              ) : (
                <div className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-black/50 border border-gold-500/20' : 'bg-white border border-gold-400/20'}`}>
                  <h2 className={`text-xl font-bold mb-6 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Hoàn thành thử thách
                  </h2>

                  {/* Upload Image */}
                  <div className="mb-6">
                    <label className={`block text-sm font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      Hình ảnh hoàn thành thử thách *
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className={`w-full p-3 rounded-lg border ${
                        theme === 'dark'
                          ? 'bg-black/50 border-gray-700 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                    {imagePreview && (
                      <div className="mt-4">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="w-full max-w-md rounded-lg"
                        />
                      </div>
                    )}
                  </div>

                  {/* Reflection */}
                  <div className="mb-6">
                    <label className={`block text-sm font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      Cảm nghĩ về thử thách này *
                    </label>
                    <textarea
                      value={reflection}
                      onChange={(e) => setReflection(e.target.value)}
                      rows={6}
                      placeholder="Chia sẻ cảm nghĩ của bạn về thử thách này..."
                      className={`w-full p-3 rounded-lg border ${
                        theme === 'dark'
                          ? 'bg-black/50 border-gray-700 text-white placeholder-gray-500'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                      }`}
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    onClick={handleSubmit}
                    disabled={submitting || !image || !reflection.trim()}
                    className={`w-full px-6 py-3 rounded-lg font-bold transition ${
                      submitting || !image || !reflection.trim()
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-gold-gradient text-white hover:shadow-lg'
                    }`}
                  >
                    {submitting ? 'Đang gửi...' : 'Gửi submission'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </main>
  )
}

